import {
  REMOTE_CONFIG_DELETE_FAILED_MESSAGE,
  REMOTE_CONFIG_GONE_MESSAGE,
  REMOTE_CONFIG_JSON_SINGLE_VALUE_MESSAGE,
  REMOTE_CONFIG_NUMBER_MESSAGE,
  REMOTE_CONFIG_SAVE_FAILED_MESSAGE,
  REMOTE_CONFIG_VALUE_TOO_LARGE_MESSAGE,
  RemoteConfigApp,
  RemoteConfigErrorCode,
  RemoteConfigValueType,
  remoteConfigAppTitles,
} from 'src/constants/remote-config/remoteConfig'
import type { RemoteConfigError, RemoteConfigFormValues } from 'src/interfaces/remoteConfig'

const KNOWN_ERROR_CODES: string[] = [
  RemoteConfigErrorCode.NotFound,
  RemoteConfigErrorCode.KeyAlreadyExists,
  RemoteConfigErrorCode.ValueTypeMismatch,
  RemoteConfigErrorCode.ValueTooLarge,
]

/** The API's domain error name from a thrown `callSecureApi` response, when it is one the modals map. */
export const resolveRemoteConfigErrorCode = (error: unknown): RemoteConfigErrorCode => {
  const errorName = (error as { payload?: { error?: unknown } } | null)?.payload?.error

  if (typeof errorName === 'string' && KNOWN_ERROR_CODES.includes(errorName)) {
    return errorName as RemoteConfigErrorCode
  }

  return RemoteConfigErrorCode.Unknown
}

export const buildRemoteConfigKeyTakenMessage = (app: RemoteConfigApp, key: string): string => {
  return `A ${remoteConfigAppTitles[app]} key named ${key} already exists`
}

export const describeRemoteConfigSaveError = (
  errorCode: unknown,
  values: Pick<RemoteConfigFormValues, 'app' | 'key' | 'type'>,
): RemoteConfigError => {
  if (errorCode === RemoteConfigErrorCode.KeyAlreadyExists) {
    return {
      target: 'key',
      message: buildRemoteConfigKeyTakenMessage(values.app, values.key.trim()),
    }
  }

  if (
    errorCode === RemoteConfigErrorCode.ValueTypeMismatch &&
    values.type === RemoteConfigValueType.Json
  ) {
    return { target: 'valueText', message: REMOTE_CONFIG_JSON_SINGLE_VALUE_MESSAGE }
  }

  if (
    errorCode === RemoteConfigErrorCode.ValueTypeMismatch &&
    values.type === RemoteConfigValueType.Number
  ) {
    return { target: 'valueText', message: REMOTE_CONFIG_NUMBER_MESSAGE }
  }

  if (errorCode === RemoteConfigErrorCode.ValueTooLarge) {
    return { target: 'valueText', message: REMOTE_CONFIG_VALUE_TOO_LARGE_MESSAGE }
  }

  if (errorCode === RemoteConfigErrorCode.NotFound) {
    return { target: 'gone', message: REMOTE_CONFIG_GONE_MESSAGE }
  }

  return { target: 'form', message: REMOTE_CONFIG_SAVE_FAILED_MESSAGE }
}

export const describeRemoteConfigDeleteError = (errorCode: unknown): RemoteConfigError => {
  if (errorCode === RemoteConfigErrorCode.NotFound) {
    return { target: 'gone', message: REMOTE_CONFIG_GONE_MESSAGE }
  }

  return { target: 'form', message: REMOTE_CONFIG_DELETE_FAILED_MESSAGE }
}
